"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { showToast } from "@/components/ui/sonner";
import { verifyEmail } from "@/lib/api/generated";
import type { VerifyEmailRequest } from "@/lib/api/model";
import { currentCometToken } from "@/lib/cometly";

export function useVerifyEmail() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    // A password login that detours to email verification returns before the
    // backend persists the login-page click token, and the /verify page has
    // no pixel — so the token snapshotted to localStorage rides the verify
    // call instead (same pattern as use-mfa-verify).
    mutationFn: (data: VerifyEmailRequest) =>
      verifyEmail({ ...data, comet_token: currentCometToken() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      // Always go straight to onboarding after email verification.
      // For invitee registration the backend already accepted the
      // invitation (WorkOS Admin POST in _register_via_invitation, plus
      // the verify-email route auto-marks the local Invitation row
      // accepted), so routing through /invite would be a redundant
      // pass-through. Login / MFA still use buildPostAuthRedirectPath
      // because /invite does real work in those paths.
      router.push("/onboarding");
    },
    onError: (error: Error) => {
      showToast.error("Verification failed", {
        description: error.message || "Verification failed.",
      });
    },
  });
}
